import type { PropertySetItemProperty } from './PropertySetItem';
import type {
  CustomEntityPropertySetsInput,
  CustomPanelProperty,
  CustomPropertySetInput,
  DisplayEntitySets,
  DisplaySet,
} from './object-properties.types';
import {
  formatCommonPropValue,
  formatDisplayValue,
  type ObjectPropertyFormattingContext,
} from './object-properties-formatting-helper';
import {
  beautifyCustomPropLink,
  formatCustomPropValue,
  getCustomPropLinkHref,
} from './custom-property-formatting-helper';
import {
  encodeCustomPropertyFavoriteKey,
  encodeModelPropertyFavoriteKey,
  safePrefixDuplicates,
  toDisplaySet,
} from './object-properties-panel-helper';
import type { ObjectPropertiesPanelData } from '@trimble/web3d-capabilities';

type CommonModelPropSet = NonNullable<
  ObjectPropertiesPanelData['commonModelPropSets']
>[number];

type ModelEntityProp = NonNullable<
  ObjectPropertiesPanelData['entityProps']
>[number];

/** Pass-through used for custom set names that should not be refined. */
export const identityName = (name: string): string => name;

export interface BuildDisplaySetsContext {
  formattingContext: ObjectPropertyFormattingContext;
  formatName(name: string): string;
  sortPropertiesByName(
    properties: readonly PropertySetItemProperty[]
  ): PropertySetItemProperty[];
}

function buildCustomSetsByEntity(
  entries: readonly CustomEntityPropertySetsInput[]
): Map<string, readonly CustomPropertySetInput[]> {
  const map = new Map<string, readonly CustomPropertySetInput[]>();

  for (const entry of entries) {
    map.set(entry.entityId, entry.psets);
  }

  return map;
}

export function mergeEntityIds(
  modelEntityProps: readonly ModelEntityProp[],
  customEntityPropertySets: readonly CustomEntityPropertySetsInput[]
): string[] {
  return [
    ...new Set([
      ...modelEntityProps.map((entity) => entity.entityId),
      ...customEntityPropertySets.map((entry) => entry.entityId),
    ]),
  ];
}

export const customDefinitionKey = (pset: CustomPropertySetInput): string =>
  `custom::${pset.libId}::${pset.defId}`;

export function toCustomPropertySetItem(
  prop: CustomPanelProperty,
  libId: string,
  defId: string,
  formatting: ObjectPropertyFormattingContext
): PropertySetItemProperty {
  const displayValue = formatCustomPropValue(prop, formatting);
  const href = getCustomPropLinkHref(displayValue);
  return {
    key: encodeCustomPropertyFavoriteKey(libId, defId, prop.id ?? prop.name),
    name: prop.name,
    value:
      href !== undefined ? beautifyCustomPropLink(displayValue) : displayValue,
    ...(href !== undefined ? { href } : {}),
  };
}

export function buildDisplayCommonSets(
  commonModelPropSets: readonly CommonModelPropSet[],
  ctx: BuildDisplaySetsContext
): DisplaySet[] {
  const setKeys = safePrefixDuplicates(
    commonModelPropSets.map((pset) => pset.name)
  );

  // Set order is applied later by `normalizePanelData` (panel-wide authority).
  return commonModelPropSets.map((pset, index) =>
    toDisplaySet({
      psetName: pset.name,
      setKey: setKeys[index],
      properties: pset.properties.map((prop) => {
        const propertyName = prop.displayProperty.name ?? '';
        return {
          key: encodeModelPropertyFavoriteKey(pset.name, propertyName),
          name: ctx.formatName(propertyName),
          value: formatCommonPropValue(prop, ctx.formattingContext),
          muted: !prop.isSameValueAcrossSelection,
        };
      }),
      formatName: ctx.formatName,
      sortPropertiesByName: ctx.sortPropertiesByName,
    })
  );
}

/**
 * @intent Combines an entity's model-derived psets with its custom psets into
 * a single display list.
 *
 * Per entity, custom sets appear first, followed by model sets. Cross-scope
 * ordering (including alphabetical interleave) is applied later by
 * `normalizePanelData`, which is the final authority when the panel renders.
 *
 * Set keys are made unique per entity via `safePrefixDuplicates`, applied
 * independently to model and custom collections so a collision within either
 * group (e.g. two custom psets sharing the same `libId::defId`) still resolves
 * to distinct keys.
 */
export function buildDisplayEntitySets(
  modelEntityProps: readonly ModelEntityProp[],
  customEntityPropertySets: readonly CustomEntityPropertySetsInput[],
  ctx: BuildDisplaySetsContext
): DisplayEntitySets[] {
  const customSetsMap = buildCustomSetsByEntity(customEntityPropertySets);
  const entityIds = mergeEntityIds(modelEntityProps, customEntityPropertySets);
  const modelEntityById = new Map(
    modelEntityProps.map((entity) => [entity.entityId, entity])
  );

  return entityIds.map((entityId) => {
    const entity = modelEntityById.get(entityId) ?? { entityId, psets: [] };

    const modelSetKeys = safePrefixDuplicates(
      entity.psets.map((pset) => pset.name)
    );

    const modelSets = entity.psets.map((pset, index) =>
      toDisplaySet({
        psetName: pset.name,
        setKey: modelSetKeys[index],
        isCustom: false,
        properties: pset.properties.map((prop) => ({
          key: encodeModelPropertyFavoriteKey(pset.name, prop.name),
          name: ctx.formatName(prop.name),
          value: formatDisplayValue(
            prop.value,
            prop.type,
            ctx.formattingContext
          ),
        })),
        formatName: ctx.formatName,
        sortPropertiesByName: ctx.sortPropertiesByName,
      })
    );

    const customPsets = customSetsMap.get(entityId) ?? [];
    const customSetKeys = safePrefixDuplicates(
      customPsets.map((pset) => customDefinitionKey(pset))
    );

    const customSets = customPsets.map((pset, index) =>
      toDisplaySet({
        psetName: pset.name,
        setKey: customSetKeys[index],
        isCustom: true,
        properties: pset.properties.map((prop) =>
          toCustomPropertySetItem(
            prop,
            pset.libId,
            pset.defId,
            ctx.formattingContext
          )
        ),
        formatName: identityName,
        sortPropertiesByName: ctx.sortPropertiesByName,
      })
    );

    return {
      entityId,
      psets: [...customSets, ...modelSets],
    };
  });
}
